import type { AxiosError } from "axios";

/** A normalized error shape you can rely on everywhere */
export class AppError extends Error {
  status?: number;
  data?: any;

  constructor(message: string, status?: number, data?: any) {
    super(message);
    this.name = "AppError";
    this.status = status;
    this.data = data;
  }
}

/** Try hard to extract a user-friendly message from different backends */
export function extractErrorMessage(err: unknown): string {
  // Axios?
  const ax = err as AxiosError<any>;
  const data = ax?.response?.data;

  // ASP.NET Web API classic often sends ExceptionMessage
  // ASP.NET Core ProblemDetails uses title/detail
  // Your API uses { message } or { Message }
  const fromKnownFields =
    data?.message ||
    data?.Message ||
    data?.ExceptionMessage ||
    data?.detail ||
    data?.title;

  // Validation dictionaries (ProblemDetails.errors or ModelState)
  const fromValidation =
    (data?.errors &&
      typeof data.errors === "object" &&
      firstValidationMessage(data.errors)) ||
    (data?.ModelState &&
      typeof data.ModelState === "object" &&
      firstValidationMessage(data.ModelState));

  const fallbackStringBody =
    typeof data === "string" && data.trim().length ? data : undefined;

  return (
    fromKnownFields ||
    fromValidation ||
    fallbackStringBody ||
    ax?.message ||
    (err instanceof Error ? err.message : undefined) ||
    "Ocurrió un error desconocido"
  );
}

function firstValidationMessage(obj: Record<string, any>): string | undefined {
  // obj like: { FieldA: ["msg1", "msg2"], FieldB: ["msg"] }
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (Array.isArray(val) && val.length && typeof val[0] === "string") {
      return val[0];
    }
    if (typeof val === "string" && val) return val;
  }
  return undefined;
}

/** Turn any thrown error into a normalized AppError */
export function toAppError(err: unknown): AppError {
  const ax = err as AxiosError<any>;
  const message = extractErrorMessage(err);
  const status = ax?.response?.status;
  const data = ax?.response?.data;
  return new AppError(message, status, data);
}
